import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useContext } from 'react';
import { Accordion, AccordionSummary, AccordionDetails, Typography, Box, } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTheme, alpha } from '@mui/material/styles';
import { FaqCtaSharedSealContext } from '../../LandingFaqCtaBand/FaqCtaBandContext';
import { FAQ_CARD_BG_IN_BAND, FAQ_CARD_BORDER } from '../../../theme/faqCtaMock';
import { sectionBackgrounds, resolveGradient } from '../../../theme';
/**
 * AccordionBlock – collapsible FAQ / Q&A sections, or `faqCards` static WV-style Q/A cards.
 * Supports label + gradient (same as Hero, CtaBlock); props match flow.json structure.
 */
const AccordionBlock = ({ label, gradient, variant = 'accordion', items = [], allowMultiple = false, questionColor, questionFontSize, answerColor, answerFontSize, backgroundColor, borderRadius = 2, boxShadow, border, borderColor, accentColor, fontSize, fontFamily, fontWeight = 600, paddingTop, paddingBottom, marginTop, marginBottom, maxWidth, componentAlignment = 'left', sectionBackground, }) => {
    const theme = useTheme();
    const inFaqCtaBand = useContext(FaqCtaSharedSealContext);
    const [expanded, setExpanded] = useState([]);
    const handleChange = (panelKey) => () => {
        setExpanded((prev) => allowMultiple
            ? prev.includes(panelKey)
                ? prev.filter((p) => p !== panelKey)
                : [...prev, panelKey]
            : prev.includes(panelKey)
                ? []
                : [panelKey]);
    };
    /** FAQ band: plain white (no cream radial) — matches mock; blocks full-page ghost seal behind this strip. */
    const background = sectionBackground !== undefined
        ? sectionBackground
        : gradient
            ? resolveGradient(gradient, sectionBackgrounds.hero)
            : backgroundColor && backgroundColor !== 'transparent'
                ? backgroundColor
                : '#ffffff';
    const itemSurfaceBg = backgroundColor && backgroundColor !== 'transparent' ? backgroundColor : undefined;
    const faqCardFill = alpha(theme.palette.primary.main, 0.065);
    const faqCardFillExpanded = alpha(theme.palette.primary.main, 0.09);
    const faqCardBorder = alpha(theme.palette.primary.main, 0.14);
    if (variant === 'faqCards') {
        const sectionBgDefault = sectionBackground !== undefined
            ? sectionBackground
            : gradient != null
                ? resolveGradient(gradient, sectionBackgrounds.hero)
                : backgroundColor && backgroundColor !== 'transparent'
                    ? backgroundColor
                    : '#ffffff';
        /** Let shared seal show in side margins (mock: watermark overlaps FAQ zone). */
        const sectionBg = inFaqCtaBand ? 'transparent' : sectionBgDefault;
        return (_jsx(Box, { component: "section", sx: {
                position: 'relative',
                background: sectionBg,
                paddingTop: paddingTop ?? { xs: 4, md: 5 },
                paddingBottom: paddingBottom ?? { xs: 3, md: 4 },
                paddingLeft: { xs: 2, sm: 3, md: 4 },
                paddingRight: { xs: 2, sm: 3, md: 4 },
                marginTop: marginTop || 0,
                marginBottom: marginBottom || 0,
            }, children: _jsxs(Box, { sx: {
                    maxWidth: maxWidth || 720,
                    mx: 'auto',
                }, children: [label && (_jsx(Typography, { variant: "h4", component: "h2", sx: {
                            fontFamily: (t) => t.typography.h4.fontFamily,
                            textAlign: 'center',
                            fontWeight: 700,
                            color: 'primary.dark',
                            mb: { xs: 2, md: 2.5 },
                            letterSpacing: '-0.02em',
                            lineHeight: 1.25,
                            fontSize: { xs: '1.5rem', md: '1.75rem' },
                        }, children: label })), _jsx(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 1.75 }, children: items.map((item, index) => (_jsxs(Box, { sx: {
                                p: { xs: 2, sm: 2.5 },
                                borderRadius: 2,
                                border: '1px solid',
                                borderColor: inFaqCtaBand ? FAQ_CARD_BORDER : borderColor || faqCardBorder,
                                bgcolor: itemSurfaceBg ?? (inFaqCtaBand ? FAQ_CARD_BG_IN_BAND : faqCardFill),
                                boxShadow: inFaqCtaBand ? 'none' : '0 1px 3px rgba(12, 38, 70, 0.07)',
                            }, children: [_jsxs(Typography, { sx: {
                                        fontFamily: (t) => t.typography.body1.fontFamily,
                                        fontWeight: 700,
                                        fontSize: questionFontSize || '1rem',
                                        color: questionColor || 'primary.dark',
                                        mb: 1,
                                        lineHeight: 1.45,
                                    }, children: ["Q: ", item.question || 'Question'] }), _jsxs(Typography, { variant: "body1", sx: {
                                        fontFamily: fontFamily || ((t) => t.typography.body1.fontFamily),
                                        fontSize: answerFontSize || fontSize || '1rem',
                                        color: answerColor || 'primary.dark',
                                        fontWeight: 400,
                                        lineHeight: 1.65,
                                        whiteSpace: 'pre-wrap',
                                    }, children: ["A: ", item.answer || 'Answer'] })] }, index))) })] }) }));
    }
    return (_jsx(Box, { component: "section", sx: {
            background,
            paddingTop: paddingTop ?? { xs: 7, md: 9 },
            paddingBottom: paddingBottom ?? { xs: 7, md: 9 },
            paddingLeft: { xs: 2, sm: 3, md: 4 },
            paddingRight: { xs: 2, sm: 3, md: 4 },
            marginTop: marginTop || 0,
            marginBottom: marginBottom || 0,
            textAlign: 'center',
        }, children: _jsxs(Box, { sx: {
                maxWidth: maxWidth || 640,
                mx: 'auto',
            }, children: [label && (_jsx(Typography, { variant: "h4", component: "h2", sx: {
                        fontFamily: (t) => t.typography.h4.fontFamily,
                        fontWeight: 700,
                        color: 'primary.dark',
                        mb: { xs: 3, md: 4 },
                        letterSpacing: '-0.02em',
                        lineHeight: 1.25,
                        fontSize: { xs: '1.5rem', md: '1.75rem' },
                        textAlign: 'center',
                    }, children: label })), _jsx(Box, { sx: { textAlign: 'left' }, children: items.map((item, index) => {
                        const panelKey = `panel-${index}`;
                        const isExpanded = expanded.includes(panelKey);
                        return (_jsxs(Accordion, { expanded: isExpanded, onChange: handleChange(panelKey), sx: {
                                mb: 2,
                                borderRadius: `${Number(borderRadius) || 8}px`,
                                '&:before': { display: 'none' },
                                /* Mock: light blue-gray Q/A cards */
                                boxShadow: boxShadow || '0 1px 3px rgba(12, 38, 70, 0.07)',
                                border: border || '1px solid',
                                borderColor: borderColor || faqCardBorder,
                                bgcolor: itemSurfaceBg ?? faqCardFill,
                                overflow: 'hidden',
                                '&.Mui-expanded': {
                                    boxShadow: boxShadow || `0 4px 12px ${accentColor || alpha(theme.palette.primary.main, 0.1)}`,
                                },
                            }, children: [_jsx(AccordionSummary, { expandIcon: _jsx(ExpandMoreIcon, { sx: { color: 'primary.main', fontSize: 22 } }), sx: {
                                        px: 2.25,
                                        py: 1.5,
                                        backgroundColor: isExpanded
                                            ? itemSurfaceBg || faqCardFillExpanded
                                            : itemSurfaceBg || faqCardFill,
                                        '&:hover': {
                                            backgroundColor: itemSurfaceBg || faqCardFillExpanded,
                                        },
                                    }, children: _jsx(Typography, { component: "span", sx: {
                                            fontWeight: Number(fontWeight) || 700,
                                            fontSize: questionFontSize || fontSize || '1rem',
                                            fontFamily: fontFamily || ((t) => t.typography.h5.fontFamily),
                                            color: questionColor || 'primary.dark',
                                            flex: 1,
                                            textAlign: 'left',
                                        }, children: item.question || 'Question' }) }), _jsx(AccordionDetails, { sx: {
                                        pt: 0,
                                        pb: 2.5,
                                        px: 2.25,
                                        bgcolor: itemSurfaceBg ?? faqCardFill,
                                        borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                                    }, children: _jsx(Typography, { variant: "body1", sx: {
                                            fontSize: answerFontSize || fontSize || '1rem',
                                            fontFamily: fontFamily || ((t) => t.typography.body1.fontFamily),
                                            fontWeight: 400,
                                            color: answerColor || 'text.secondary',
                                            lineHeight: 1.65,
                                            whiteSpace: 'pre-wrap',
                                            textAlign: 'left',
                                        }, children: item.answer || 'Answer' }) })] }, index));
                    }) })] }) }));
};
export default AccordionBlock;
